/**
 * The imports conversation on the import hub (Phase 5; docs/design/2026-09-24_async-copilot.md): ask the brain
 * about this org's imports, or send it files. Each file enters the import intake like a hub upload, and its card
 * here shows the job's live status, from the job itself, never from the brain's words. A reader waiting for
 * approval links to the hub's readers: approval stays a person's act there. A document family's file goes to its
 * product instead (the documents lane, migration 0228): its card says where it was sent. The brain answers from the
 * org's import records only, and its reply is shown as plain text.
 *
 * The same conversation is the AI Casting Consultant's page (routes/twin/copilot.tsx). Both show it where the platform
 * answers the thread list (lib/copilot/availability), never by a build flag (2026-09-26). The pieces are pure (rendered in tests
 * without providers); the container at the bottom holds the hooks, and its decisions are the functions of
 * lib/imports/copilot (conversationState, sendAndConfirm, keptAfterSend), tested there.
 *
 * The interactive copilot (contract v1, 2026-09-26): under a brain message that asks back, an ask card sends the
 * person's answer; under one that proposes a change, a proposal card lets them approve it, which their browser applies
 * through the platform's own create routes, as them, or reject it (imports-copilot-cards.tsx; the decisions are
 * lib/imports/copilot-blocks and copilot-proposals). Starter questions sit above the composer; the panel's are made from
 * the plant's own brief (lib/copilot/starters, 2026-10-28).
 *
 * The plant's corrections (L2, the 2026-10-01 learning-loop plan): under a brain message that heard a person say a
 * recommendation is wrong, a correction card records it as theirs ("Record this correction", via this thread); under
 * one that read their words as the answer to an open plant question, "Record as answer" records it on the question.
 * The consultant records nothing; a rejected proposal takes the person's reason, optionally.
 *
 * Asked from a page (2026-09-28; lib/copilot/ask, imports-copilot-context.tsx): a page's "Ask" link opens the
 * consultant's page with what the question is about, shown as a chip above the composer; the first message the person
 * then sends carries it (outgoingPost), and the thread says under that message what it was about. On the
 * consultant's page, quick questions draft a question and set its context while the composer is empty.
 */
import { Fragment, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { composerHasDraft } from "@/lib/copilot/panel-state";
import { askedOnText, briefChangedAfter, briefChangedText } from "@/lib/copilot/turn-dates";
import { Link, useNavigate } from "react-router-dom";
import { Bot, Loader2, MessageSquarePlus, Paperclip, RefreshCw, Send, User } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card } from "@/lib/ui/card";
import { EmptyState } from "@/lib/ui/empty-state";
import { cn } from "@/lib/ui/cn";
import { useOrg } from "@/lib/org-context";
import { useImportJobsQuery, type ImportJob } from "@/lib/api/imports";
import {
  useCopilotCardUnits,
  useCopilotMessagesQuery,
  useCopilotTagWords,
  useCopilotThreadsQuery,
  useCreateCopilotThread,
  usePatchProposal,
  usePostCopilotMessage,
  useProposalCalls,
  useRecheckThread,
  useReloadThread,
  type AlarmRuleSpec,
  type CopilotAnswerBlock,
  type CopilotAnswerValues,
  type CopilotAskBlock,
  type CopilotCorrectionBlock,
  type CopilotMessage,
  type CopilotPlantAnswerBlock,
  type CopilotProposalBlock,
} from "@/lib/api/copilot-imports";
import { useRecordRecommendationEvent } from "@/lib/api/recommendations";
import { useAnswerQuestion } from "@/lib/api/twin-knowledge";
import { correctionRequest, recordThenReload } from "@/lib/copilot/corrections";
import { READERS_HREF, READERS_SECTION_ID, jobBadgeOf, jobStateText, scrollToSection } from "@/lib/imports/hub";
import {
  startersShown,
  FILE_HINT,
  awaitingBrain,
  composerProblem,
  conversationState,
  keptAfterSend,
  messageStatusText,
  newThreadTitle,
  newestThread,
  outgoingPost,
  refusalOf,
  sendAndConfirm,
  shownThread,
  starterMessage,
  type ConversationState,
  type OutgoingMessage,
  type Refusal,
  type SendOutcome,
} from "@/lib/imports/copilot";
import { answerKey, answerText, answeredAsks, blocksOf, contextOfMessage } from "@/lib/imports/copilot-blocks";
import type { CopilotAbout } from "@/lib/copilot/ask";
import { focusStillOurs, type ElementLike } from "@/lib/copilot/selection";
import type { QuickAsk } from "@/lib/brief/ask";
import {
  IDLE_PROPOSAL,
  approveBlocked,
  approveProposal,
  localAfter,
  localBusy,
  needsStoredUnit,
  planApply,
  proposalSpecText,
  proposalView,
  rejectProposal,
  saveApplied,
  type ActionResult,
  type ApplyPlan,
  type CardUnits,
  type ProposalLocal,
  type TagWords,
} from "@/lib/imports/copilot-proposals";
import { useAcquisitionStudyQuery, useReadAcquisitionStudy } from "@/lib/api/acquisition";
import {
  CARD_TIME_UNKNOWN,
  NOTHING_TO_APPLY,
  appliedWords,
  cardTime,
  changeApplyPlan,
  changeLines,
  changeViews,
  changesOf,
} from "@/lib/acquisition/consultant";
import {
  AcquisitionChangeCard,
  AskCard,
  CorrectionCard,
  ErrorCode,
  PlantAnswerCard,
  ProposalCard,
  StarterChips,
} from "./imports-copilot-cards";
import { AboutLine, ContextChip, QuickAsks } from "./imports-copilot-context";
import { OrgUnits } from "@/components/units/OrgUnits";
import { useDisplayUnits } from "@/lib/api/display-units";
import { useShownPrefs } from "@/lib/units/context";

const ACCEPT = ".csv,.tsv,.txt,.xls,.xlsx,.xlsm,.htm,.html,.xml,.json,.zip";
const UNAVAILABLE = "Asking the consultant is not available here yet.";

function Attachments({ message, jobsById }: { message: CopilotMessage; jobsById: Record<string, ImportJob> }) {
  if (message.attachments.length === 0) return null;
  return (
    <ul className="mt-2 flex flex-col gap-1.5">
      {message.attachments.map((a, i) => {
        // A file the documents lane sent to a product has no job (migration 0228): it says where it went, and links
        // nowhere into import. A job held for that product, or sent there once the org holds it, says so too.
        const job = a.job_id ? jobsById[a.job_id] : undefined;
        return (
          <li
            key={a.job_id ?? a.document_id ?? `file-${i}`}
            className="flex min-w-0 flex-wrap items-center gap-2 rounded-md border border-border px-2 py-1 text-xs"
          >
            <Paperclip className="size-3 shrink-0 text-on-surface-muted" />
            <span className="min-w-0 break-all font-medium text-on-surface">{a.filename}</span>
            {a.routed && <Badge variant="info">Sent to {a.routed.product_name}</Badge>}
            {job && <Badge variant={jobBadgeOf(job)}>{jobStateText(job)}</Badge>}
            {job?.status === "failed" && job.error && <span className="break-words text-on-surface-muted">{job.error}</span>}
            {job?.status === "awaiting_approval" && (
              // The router changes the hash but never scrolls to it: the link scrolls to the readers itself (#843 review N4).
              <Link
                to={READERS_HREF}
                onClick={() => scrollToSection(document, READERS_SECTION_ID)}
                className="font-medium text-brand underline"
              >
                Review and approve
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * What the thread's cards can do, from the container: answer an ask, and approve, reject or save a proposal. Without
 * it (a thread shown read-only) the cards show their state and offer nothing.
 */
export interface ThreadActions {
  /** The person may post (canAskTheBrain): they may answer an ask. */
  canAnswer: boolean;
  /** A message goes up from this page: every Send waits. */
  sending: boolean;
  /** The ask whose answer goes up (answerKey). */
  answering: string | null;
  /** Why the server refused an ask's answer, by answerKey. */
  answerErrors: Record<string, Refusal>;
  /** Answers sent from this page that the thread does not show yet: their words, by answerKey. */
  sentAnswers: Record<string, string>;
  onAnswer: (message: CopilotMessage, ask: CopilotAskBlock, values: CopilotAnswerValues) => void;
  /** Proposals can be decided here: without it, their cards show their state and no buttons. */
  decide: boolean;
  /** This page's state of each proposal it acted on, by proposal_id. */
  proposals: Record<string, ProposalLocal>;
  /** The person's permissions (useOrg().can). */
  can: (perm: string) => boolean;
  /** The org's tags by id, once known: an alarm proposal's card names its tag. */
  tags: Record<string, TagWords>;
  /**
   * The units a card's numbers are stored in, once the page has read them (units slice 2d): a card in the person's
   * own unit is set in its tag's or its parameter's unit. Without them, an alarm in the person's unit waits, with
   * Approve off.
   */
  units?: Pick<CardUnits, "tagUnit" | "conditionUnit">;
  onApprove: (block: CopilotProposalBlock) => void;
  /** Rejects a proposal, with the person's reason ("" for none). */
  onReject: (block: CopilotProposalBlock, reason: string) => void;
  onSave: (block: CopilotProposalBlock) => void;
  /** The thread shown: a correction recorded from it says so (via copilot:<thread>). Null for none. */
  threadId: string | null;
  /** The person may give a verdict and answer the plant's questions (knowledge.respond). */
  canRespond: boolean;
  /** The correction and plant-answer cards being recorded, and why one was not, by cardKey. */
  recording: Record<string, boolean>;
  cardErrors: Record<string, string>;
  onRecordCorrection: (message: CopilotMessage, block: CopilotCorrectionBlock, reason: string) => void;
  onRecordAnswer: (message: CopilotMessage, block: CopilotPlantAnswerBlock, text: string) => void;
}

/** A correction's or a plant answer's key in a thread: the brain message that carries it, and its id. */
function cardKey(messageId: string, blockId: string): string {
  return `${messageId}/${blockId}`;
}

const READ_ONLY: ThreadActions = {
  canAnswer: false,
  sending: false,
  answering: null,
  answerErrors: {},
  sentAnswers: {},
  onAnswer: () => {},
  decide: false,
  proposals: {},
  can: () => false,
  tags: {},
  onApprove: () => {},
  onReject: () => {},
  onSave: () => {},
  threadId: null,
  canRespond: false,
  recording: {},
  cardErrors: {},
  onRecordCorrection: () => {},
  onRecordAnswer: () => {},
};

/**
 * An Acquisition study's decisions card (acquisition_change, P3c), with the study read (any member reads it), so each
 * change says what becomes of it when applied against the rows as they stand (lib/acquisition/consultant). An org admin
 * applies or dismisses it; anyone else reads it.
 */
function AcquisitionChangeBlock({
  message,
  block,
  actions,
}: {
  message: CopilotMessage;
  block: Extract<CopilotProposalBlock, { kind: "acquisition_change" }>;
  actions: ThreadActions;
}) {
  const spec = block.spec as unknown as Record<string, unknown>;
  const studyId = typeof spec.study_id === "string" ? spec.study_id : undefined;
  const study = useAcquisitionStudyQuery(studyId);
  const changes = useMemo(() => changesOf(spec.changes) ?? [], [spec.changes]);
  // The card is compared against the study as the consultant read it (its read_at), else as of its message.
  const since = cardTime(spec, message.created_at);
  const lines = useMemo(() => {
    const rows = study.data?.rows;
    if (!rows) return changeLines(changes, null);
    const names = new Map(rows.map((r) => [r.point_key, r.name] as const));
    return changeLines(changes, changeViews(changes, rows, since), { point: (k) => names.get(k) ?? k });
  }, [changes, study.data, since]);
  const local = (block.proposal_id && actions.proposals[block.proposal_id]) || IDLE_PROPOSAL;
  const view = proposalView(block, local, approveBlocked(block, actions.can));
  return (
    <AcquisitionChangeCard
      block={block}
      view={view}
      studyName={study.data?.source.name ?? null}
      lines={lines}
      canApply={actions.decide && actions.can("org.admin")}
      onApply={() => actions.onApprove(block)}
      onDismiss={() => actions.onReject(block, "")}
      onSave={() => actions.onSave(block)}
    />
  );
}

/** A brain message's asks, proposals, corrections and plant answers, as cards under its words. */
function MessageBlocks({
  message,
  answered,
  actions,
}: {
  message: CopilotMessage;
  answered: ReturnType<typeof answeredAsks>;
  actions: ThreadActions;
}) {
  const prefs = useShownPrefs();
  const blocks = blocksOf(message);
  if (blocks.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-2">
      {blocks.map((b, i) => {
        if (b.type === "ask") {
          const key = answerKey(message.id, b.id);
          const done = answered.get(key);
          return (
            <AskCard
              key={`ask-${b.id}-${i}`}
              ask={b}
              idPrefix={`ask-${message.id}-${b.id}`}
              answered={done ? done.body || answerText(b, done.values) : (actions.sentAnswers[key] ?? null)}
              canAnswer={actions.canAnswer}
              sending={actions.answering === key}
              busy={actions.sending}
              error={actions.answerErrors[key] ?? null}
              onAnswer={(values) => actions.onAnswer(message, b, values)}
            />
          );
        }
        if (b.type === "proposal" && b.kind === "acquisition_change") {
          return <AcquisitionChangeBlock key={`proposal-${b.id}-${i}`} message={message} block={b} actions={actions} />;
        }
        if (b.type === "proposal") {
          const local = (b.proposal_id && actions.proposals[b.proposal_id]) || IDLE_PROPOSAL;
          const tag = b.kind === "alarm_rule" ? actions.tags[(b.spec as AlarmRuleSpec).tag_id] : undefined;
          const units: CardUnits = { ...actions.units, prefs };
          const view = proposalView(b, local, approveBlocked(b, actions.can, units));
          return (
            <ProposalCard
              key={`proposal-${b.id}-${i}`}
              block={b}
              view={actions.decide ? view : { ...view, approve: null, reject: null, save: null, note: null }}
              specText={proposalSpecText(b, tag, prefs, units)}
              onApprove={() => actions.onApprove(b)}
              onReject={(reason) => actions.onReject(b, reason)}
              onSave={() => actions.onSave(b)}
            />
          );
        }
        if (b.type === "correction") {
          const key = cardKey(message.id, b.id);
          // A thread shown read-only (no actions: no thread of this page's, nothing to decide) shows the card as it is.
          const readOnly = !actions.decide || actions.threadId === null;
          return (
            <CorrectionCard
              key={`correction-${b.id}-${i}`}
              block={b}
              threadId={actions.threadId}
              readOnly={readOnly}
              canRecord={actions.decide && actions.canRespond}
              busy={actions.recording[key] === true}
              error={actions.cardErrors[key] ?? null}
              onRecord={(reason) => actions.onRecordCorrection(message, b, reason)}
            />
          );
        }
        if (b.type === "plant_answer") {
          const key = cardKey(message.id, b.id);
          return (
            <PlantAnswerCard
              key={`plant-answer-${b.id}-${i}`}
              block={b}
              readOnly={!actions.decide || actions.threadId === null}
              canRecord={actions.decide && actions.canRespond}
              busy={actions.recording[key] === true}
              error={actions.cardErrors[key] ?? null}
              onRecord={(text) => actions.onRecordAnswer(message, b, text)}
            />
          );
        }
        return null;
      })}
    </div>
  );
}

export function ImportsConversationView({
  messages,
  jobsById,
  actions = READ_ONLY,
  briefAt,
  now,
}: {
  messages: CopilotMessage[];
  jobsById: Record<string, ImportJob>;
  actions?: ThreadActions;
  /**
   * When the plant brief was last brought up to date (its run's end), on the consultant's page: a note goes after the
   * last message written before it (the 2026-10-24 sweep, item 4.3). None elsewhere.
   */
  briefAt?: string;
  /** The time an old question's day is read against; now by default. */
  now?: Date;
}) {
  const answered = answeredAsks(messages);
  const changedAfter = briefAt === undefined ? -1 : briefChangedAfter(messages, briefAt);
  return (
    // A reply that arrives is read out: the list is a polite live region of added messages.
    <ol className="flex flex-col gap-3" aria-live="polite" aria-relevant="additions">
      {messages.map((m, i) => {
        const mine = m.role === "user";
        const status = messageStatusText(m);
        // An old question says the day it was asked (4.3).
        const asked = mine ? askedOnText(m.created_at, now) : null;
        return (
          <Fragment key={m.id}>
            <li className={cn("flex gap-2", mine ? "flex-row-reverse" : "flex-row")}>
              <div
                className={cn(
                  "flex size-6 shrink-0 items-center justify-center rounded-full",
                  mine ? "bg-surface-muted text-on-surface" : "bg-brand text-brand-foreground",
                )}
                aria-hidden
              >
                {mine ? <User className="size-3.5" /> : <Bot className="size-3.5" />}
              </div>
              <div className={cn("min-w-0 max-w-[85%] rounded-lg border border-border px-3 py-2", mine ? "bg-surface" : "bg-surface-elevated")}>
                <p className="sr-only">{mine ? "You" : "The consultant"}</p>
                {m.body && <p className="whitespace-pre-wrap break-words text-sm text-on-surface">{m.body}</p>}
                <Attachments message={m} jobsById={jobsById} />
                {!mine && <MessageBlocks message={m} answered={answered} actions={actions} />}
                {mine && <AboutLine about={contextOfMessage(m)} />}
                {status && (
                  <p className={cn("mt-1 text-xs", m.status === "failed" ? "text-danger" : "text-on-surface-muted")}>
                    {m.status === "pending" || m.status === "claimed" ? <Loader2 className="mr-1 inline size-3 animate-spin" /> : null}
                    {status}
                  </p>
                )}
                {asked && (
                  <p data-asked-on="" className="mt-1 text-xs text-on-surface-muted">
                    {asked}
                  </p>
                )}
              </div>
            </li>
            {i === changedAfter && briefAt !== undefined && (
              <li data-brief-changed="" role="note" className="rounded-md bg-surface-muted px-3 py-2 text-xs text-on-surface-muted">
                {briefChangedText(briefAt)}
              </li>
            )}
          </Fragment>
        );
      })}
    </ol>
  );
}

/** Why a load failed, with Try again: alone when nothing loaded, else above the thread, which stays (#843 review N1). */
function LoadFailure({
  lead,
  error,
  autoRetry,
  onRetry,
  retrying,
}: {
  lead: string;
  error: Refusal;
  autoRetry: boolean;
  onRetry: () => void;
  retrying: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-danger/40 bg-danger/5 px-3 py-2">
      <div className="min-w-0 flex-1">
        <p role="alert" className="text-sm text-danger">
          {`${lead} ${error.text}`}
          <ErrorCode code={error.code} />
        </p>
        {autoRetry && (
          <p className="text-xs text-on-surface-muted">The page checks again every minute while a message waits for the consultant.</p>
        )}
      </div>
      <Button type="button" size="sm" variant="outline" onClick={onRetry} disabled={retrying}>
        {retrying ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
        Try again
      </Button>
    </div>
  );
}

/** What the conversation shows above the composer: the thread, or why there is none to show (conversationState). */
export function ConversationBody({
  state,
  messages,
  jobsById,
  canAsk,
  onRetry,
  retrying = false,
  actions,
  briefAt,
}: {
  state: ConversationState;
  messages: CopilotMessage[];
  jobsById: Record<string, ImportJob>;
  canAsk: boolean;
  onRetry: () => void;
  /** A load runs again: Try again waits for it. */
  retrying?: boolean;
  /** What the thread's cards can do (the container's); read-only without it. */
  actions?: ThreadActions;
  /** When the plant brief was last brought up to date: where the thread read an older brief, a note says so (4.3). */
  briefAt?: string;
}) {
  if (state.body === "unavailable") {
    return <EmptyState icon={Bot} title={UNAVAILABLE} description="The AI Casting Consultant is not switched on for this platform." />;
  }
  if (state.body === "failed") {
    return (
      <LoadFailure
        lead="The conversation could not be loaded."
        error={state.error}
        autoRetry={false}
        onRetry={onRetry}
        retrying={retrying}
      />
    );
  }
  if (state.body === "loading") {
    return (
      <p className="flex items-center gap-2 text-sm text-on-surface-muted">
        <Loader2 className="size-3.5 animate-spin" /> Loading the conversation
      </p>
    );
  }
  const failure = state.error && (
    <LoadFailure
      lead="The conversation could not be refreshed."
      error={state.error}
      autoRetry={state.autoRetry}
      onRetry={onRetry}
      retrying={retrying}
    />
  );
  if (messages.length === 0) {
    return (
      <>
        {failure}
        <EmptyState
          icon={Bot}
          title="No questions yet"
          description={
            canAsk
              ? `Ask about your plant's data, a report or an alarm, or send an export here (${FILE_HINT} per message): the consultant answers from this organisation's records, and a file becomes an import.`
              : "Your role here can read this conversation but not ask the consultant."
          }
        />
      </>
    );
  }
  return (
    <>
      {failure}
      <ImportsConversationView messages={messages} jobsById={jobsById} actions={actions} briefAt={briefAt} />
      {awaitingBrain(messages) && (
        <p className="text-xs text-on-surface-muted">The consultant usually answers within a minute or two.</p>
      )}
    </>
  );
}

/** The composer. A problem with what is written or attached, and a refusal from the server, show inline. */
export function Composer({
  text,
  onText,
  onFile,
  fileRef,
  textRef,
  problem,
  touched,
  sending,
  busy = false,
  sendError,
  onSend,
  idBase = "import-copilot",
}: {
  text: string;
  onText: (t: string) => void;
  onFile: (f: File | null) => void;
  fileRef?: RefObject<HTMLInputElement | null>;
  textRef?: RefObject<HTMLTextAreaElement | null>;
  problem: string | null;
  touched: boolean;
  sending: boolean;
  /** Another message goes up from this page (a starter, an answer): Send waits for it. */
  busy?: boolean;
  sendError: Refusal | null;
  onSend: () => void;
  /** The ids of its fields: two conversations on one screen (the page's, the panel's) need two sets. */
  idBase?: string;
}) {
  const shown = sendError?.text ?? (touched ? problem : null);
  return (
    <form
      className="flex flex-col gap-2 border-t border-border pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSend();
      }}
    >
      <label htmlFor={`${idBase}-text`} className="sr-only">
        Your message
      </label>
      <textarea
        id={`${idBase}-text`}
        ref={textRef}
        value={text}
        onChange={(e) => onText(e.target.value)}
        rows={3}
        maxLength={8000}
        placeholder="Did last week's mixer log import? Which days were new?"
        aria-describedby={shown ? `${idBase}-problem` : undefined}
        className="w-full resize-y rounded-md border border-border bg-surface px-3 py-2 text-sm text-on-surface"
      />
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={`${idBase}-files`} className="text-xs text-on-surface-muted">
          Attach an export ({FILE_HINT})
        </label>
        <input
          id={`${idBase}-files`}
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          onChange={(e) => onFile(e.target.files?.[0] ?? null)}
          className="min-w-0 text-xs"
        />
        <Button type="submit" size="sm" className="ml-auto" disabled={sending || busy || problem !== null}>
          {sending ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
          {sending ? "Sending" : "Send"}
        </Button>
      </div>
      {shown && (
        <p id={`${idBase}-problem`} role={sendError ? "alert" : undefined}
           className={cn("text-xs", sendError ? "text-danger" : "text-on-surface-muted")}>
          {shown}
          {sendError && <ErrorCode code={sendError.code} />}
        </p>
      )}
    </form>
  );
}

/** An object without one key (the answer errors, once an answer is sent again). */
function without<T>(all: Record<string, T>, key: string): Record<string, T> {
  const out = { ...all };
  delete out[key];
  return out;
}

export function ImportsConversation({
  canAsk,
  heading = true,
  draft = "",
  context: linked = null,
  quickAsks = [],
  starters,
  contained = false,
  onDraftChange,
  briefAt,
}: {
  canAsk: boolean;
  /** Its own heading, as a section of the import hub; the consultant's page has the page's. */
  heading?: boolean;
  /** A question drafted in the composer when the conversation opens (askDraftOf); the person sends it. */
  draft?: string;
  /** What a page's link says the question is about (aboutOf): the chip above the composer, until a message carries it. */
  context?: CopilotAbout | null;
  /** Quick questions offered while the composer is empty and no chip is set (the consultant's page). */
  quickAsks?: QuickAsk[];
  /**
   * The starter questions to offer, each sent as the person's question when tapped (the panel's, made from the plant's
   * own brief: lib/copilot/starters plantStarters). Absent: the fixed starters beside the quick questions.
   */
  starters?: readonly string[];
  /**
   * Fills the height it is given instead of growing with the thread (the right-hand panel, next phase, step 3): the
   * thread scrolls inside, to its newest message, and the composer stays in view below it.
   */
  contained?: boolean;
  /** Told whether the composer holds a message not yet sent (text or a file): the panel holds a new ask while it does. */
  onDraftChange?: (hasDraft: boolean) => void;
  /** When the plant brief was last brought up to date (the consultant's page): older answers say so (4.3). */
  briefAt?: string;
}) {
  const threads = useCopilotThreadsQuery();
  const createThread = useCreateCopilotThread();
  const post = usePostCopilotMessage();
  const recheck = useRecheckThread();
  const reload = useReloadThread();
  const patchProposal = usePatchProposal();
  const proposalCalls = useProposalCalls();
  const recordEvent = useRecordRecommendationEvent();
  const answerQuestion = useAnswerQuestion();
  const readStudy = useReadAcquisitionStudy();
  const navigate = useNavigate();
  const { can } = useOrg();
  const jobs = useImportJobsQuery();
  const [text, setText] = useState(draft);
  const [file, setFile] = useState<File | null>(null);
  const hasDraft = composerHasDraft(text, draft, file !== null);
  useEffect(() => {
    onDraftChange?.(hasDraft);
  }, [hasDraft, onDraftChange]);
  // What the next message is about (the chip): from a page's link or a quick question, until a message carries it.
  const [context, setContext] = useState<CopilotAbout | null>(linked);
  const [sendError, setSendError] = useState<Refusal | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  // A page's Ask link (a drafted question or its context) asks for a fresh thread, as New conversation does: the
  // question is not asked inside an unrelated conversation (shownThread).
  const [fresh, setFresh] = useState(linked !== null || draft.trim() !== "");
  // What goes up, from Send until this page knows what the send came to (the thread's second read included): the
  // composer's message, a starter, or an ask's answer ("ask:" and its answerKey). One at a time: every Send waits.
  const [sendingWhat, setSendingWhat] = useState<string | null>(null);
  const sending = sendingWhat !== null;
  const [answerErrors, setAnswerErrors] = useState<Record<string, Refusal>>({});
  // An answer that went, shown on its card until the thread's next read holds it.
  const [sentAnswers, setSentAnswers] = useState<Record<string, string>>({});
  // This page's state of each proposal it acted on, by proposal_id (the server's state is in the thread).
  const [proposals, setProposals] = useState<Record<string, ProposalLocal>>({});
  // The correction and plant-answer cards being recorded, and why one was not, by cardKey (L2).
  const [recording, setRecording] = useState<Record<string, boolean>>({});
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});
  // The proposals with an action running, marked at once on the click: a second click before the card re-renders
  // with its buttons held starts nothing (the create, then the outcome, run once).
  const inFlight = useRef(new Set<string>());
  const fileRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const idBase = contained ? "consultant-panel" : "import-copilot";
  const threadId = shownThread({ fresh, createdId, newest: newestThread(threads.data?.items ?? [])?.id ?? null });
  const messages = useCopilotMessagesQuery(threadId);
  const items = useMemo(() => messages.data?.items ?? [], [messages.data]);
  const jobsById = useMemo(
    () => Object.fromEntries((jobs.data?.items ?? []).map((j) => [j.id, j] as const)),
    [jobs.data],
  );
  // An alarm proposal's card names its tag once the org's tag list is read: only a thread that shows one reads it.
  const alarmShown = useMemo(
    () => items.some((m) => m.role === "brain" && blocksOf(m).some((b) => b.type === "proposal" && b.kind === "alarm_rule")),
    [items],
  );
  const tags = useCopilotTagWords(alarmShown);
  // A card in the person's own units is set in the unit its numbers are stored in (units slice 2d): the page reads the
  // tags, or the parameters the plant records, only while the thread shows a card that needs them (needsStoredUnit).
  const shownPrefs = useDisplayUnits().shown;
  const needed = useMemo(() => {
    const kinds = new Set(
      items.flatMap((m) => (m.role === "brain" ? blocksOf(m) : []))
        .map((b) => (b.type === "proposal" ? needsStoredUnit(b, shownPrefs) : null)),
    );
    return { tag: kinds.has("tag"), condition: kinds.has("condition") };
  }, [items, shownPrefs]);
  const cardUnits = useCopilotCardUnits(needed.tag, needed.condition);
  // A contained thread scrolls inside: to its newest message when one is added, and when it first shows.
  const shownCount = items.length;
  useEffect(() => {
    const box = threadRef.current;
    if (contained && box) box.scrollTop = box.scrollHeight;
  }, [contained, shownCount, threadId]);
  const files = file ? [file] : [];
  const problem = composerProblem(text, files);
  // React Query keeps a load's data when a later fetch fails: the state keeps the thread and the composer then, with
  // the error above them (#843 review N1).
  const state = conversationState({
    threads: { data: threads.data !== undefined, error: threads.error },
    threadId,
    messages: { data: messages.data !== undefined, error: messages.error },
    items,
    loading: threads.isLoading || messages.isLoading,
    canAsk,
  });

  // A question a page asked the panel with is drafted for the person to read and send: once the composer shows, it
  // takes the focus (once), so they can edit it or send it from the keyboard; but never from where the person moved it
  // while the threads loaded (#1186 review LOW 3).
  const focusOnShow = useRef(contained && (draft.trim() !== "" || linked !== null));
  // What had the focus when the question came: a card's Ask link keeps it after a click, and the question may take it
  // from there (#1195 review MEDIUM).
  const opener = useRef(typeof document === "undefined" ? null : document.activeElement);
  const composerShown = state.composer;
  useEffect(() => {
    if (!focusOnShow.current || !composerShown) return;
    focusOnShow.current = false;
    if (focusStillOurs(document.activeElement as ElementLike | null, opener.current as ElementLike | null)) {
      textRef.current?.focus();
    }
  }, [composerShown]);

  // Try again loads what failed once more (a failed poll also asks again every minute by itself while a message waits).
  const retry = () => {
    if (threads.error) void threads.refetch();
    if (messages.error) void messages.refetch();
  };

  /**
   * Posts a message and says what it came to. A post that got no answer is checked against the thread before this
   * page says anything, and is never sent again by itself (#868 review).
   */
  const sendMessage = async (
    what: string,
    sent: { text: string; fileName: string | null },
    postTo: (threadId: string) => Promise<unknown>,
  ): Promise<SendOutcome> => {
    setSendingWhat(what);
    try {
      return await sendAndConfirm({
        threadId,
        sent,
        // The thread as it stood, to tell the message from an earlier one with the same words: none before the
        // person's first thread, unknown while its messages had not loaded.
        before: threadId === null ? [] : messages.data ? items : null,
        post: postTo,
        start: () => createThread.mutateAsync(newThreadTitle(context, sent.text)),
        keep: setCreatedId,
        recheck,
      });
    } finally {
      setSendingWhat(null);
    }
  };

  /**
   * Posts a message as it goes up with the chip's context (outgoingPost), and clears the chip once a message that
   * carried it went: a context set while the message went up stays (keptAfterSend). A message with a file carries
   * none, so the chip waits for the next message of words.
   */
  const postOutgoing = async (what: string, m: Omit<OutgoingMessage, "threadId">): Promise<SendOutcome> => {
    const { carried } = outgoingPost({ ...m, threadId: "" });
    const outcome = await sendMessage(what, { text: m.text, fileName: m.file?.name ?? null }, (id) =>
      post.mutateAsync(outgoingPost({ ...m, threadId: id }).post),
    );
    if (outcome.kind === "sent" && carried) setContext((now) => keptAfterSend(carried, now, null));
    return outcome;
  };

  const send = async () => {
    if (problem || sending) return;
    const sentText = text;
    const sentFile = file;
    setSendError(null);
    try {
      const outcome = await postOutgoing("composer", { text: sentText.trim(), file: sentFile, context, composer: true });
      if (outcome.kind === "error") {
        setSendError(outcome.error); // what was typed and attached stays in the composer
        return;
      }
      // Only what was sent clears: text typed and a file picked while it went up stay (#843 review LOW-7). The
      // picker is emptied while it still holds the sent file, so the same file can be picked again (LOW-8).
      setText((now) => keptAfterSend(sentText, now, ""));
      setFile((now) => keptAfterSend(sentFile, now, null));
      const picker = fileRef.current;
      if (picker && keptAfterSend(sentFile, picker.files?.[0] ?? null, null) === null) picker.value = "";
    } finally {
      textRef.current?.focus();          // focus stays in the composer while Send is disabled (LOW-10)
    }
  };

  /** A starter question, sent as the person's message (JSON form), with the chip's context; the composer stays. */
  const sendStarter = async (words: string) => {
    if (sending) return;
    setSendError(null);
    const outcome = await postOutgoing("starter", starterMessage(words, context));
    if (outcome.kind === "error") setSendError(outcome.error);
  };

  /** An ask's answer: the person's message, its words (answerText) with the answer block, and the chip's context. */
  const sendAnswer = async (message: CopilotMessage, ask: CopilotAskBlock, values: CopilotAnswerValues) => {
    if (sending) return;
    const key = answerKey(message.id, ask.id);
    const words = answerText(ask, values);
    const block: CopilotAnswerBlock = { type: "answer", message_id: message.id, ask_id: ask.id, values };
    setAnswerErrors((all) => without(all, key));
    const outcome = await postOutgoing(`ask:${key}`, { text: words, answer: block, context });
    if (outcome.kind === "error") setAnswerErrors((all) => ({ ...all, [key]: outcome.error }));
    else setSentAnswers((all) => ({ ...all, [key]: words }));
  };

  const setProposal = (id: string, next: ProposalLocal | null) =>
    setProposals((all) => (next ? { ...all, [id]: next } : without(all, id)));

  /**
   * Applies an Acquisition study's decisions card (P3c): the study is read again now, and only the changes that still
   * apply to its rows go (changeApplyPlan: a row decided otherwise since the consultant read the study, the card's
   * read_at, or already so, is skipped), through PUT .../decisions as the person, then the outcome is recorded as any
   * card's (approveProposal), with how many rows it applied and skipped. A card whose time cannot be read (no read_at,
   * and its message not found in the thread) applies nothing, and says so.
   */
  const applyStudyChanges = async (
    block: CopilotProposalBlock,
    plan: Extract<ApplyPlan, { kind: "acquisition_change" }>,
    deps: Parameters<typeof approveProposal>[1],
  ): Promise<ActionResult> => {
    let rows;
    try {
      rows = (await readStudy(plan.studyId)).rows;
    } catch (err) {
      return { kind: "failed", error: refusalOf(err, "The study could not be read.") };
    }
    const made = items.find((m) =>
      blocksOf(m).some((b) => b.type === "proposal" && b.proposal_id === block.proposal_id),
    )?.created_at;
    const spec = block.spec as unknown as Record<string, unknown>;
    const changes = changesOf(spec.changes) ?? [];
    const { send, skipped, timeUnknown } = changeApplyPlan(changes, rows, cardTime(spec, made));
    if (timeUnknown) return { kind: "failed", error: { text: CARD_TIME_UNKNOWN, code: null } };
    if (send.length === 0) return { kind: "failed", error: { text: NOTHING_TO_APPLY, code: null } };
    return approveProposal({ ...plan, decisions: send, summary: appliedWords(send.length, skipped) }, deps);
  };

  /**
   * Approves, rejects, or saves (records a change already made) a proposal, as the person, and shows what it came
   * to: the recorded state patched into the thread, the thread read again when it was decided elsewhere, or this
   * page's own state (lib/imports/copilot-proposals decides each).
   */
  const act = async (block: CopilotProposalBlock, action: "approve" | "reject" | "save", reason = "") => {
    const id = block.proposal_id;
    const thread = threadId;
    const prev = id ? proposals[id] : undefined;
    if (!id || thread === null || prev?.busy || inFlight.current.has(id)) return;
    if (action === "save" && !prev?.unrecorded) return;
    // Approve and Reject stay held from the click until the outcome is recorded, or the thread read again.
    inFlight.current.add(id);
    setProposal(id, localBusy(prev, action));
    try {
      const deps = { ...proposalCalls(block, thread), open: (url: string) => navigate(url) };
      let result: ActionResult;
      try {
        if (action === "reject") result = await rejectProposal(deps, reason);
        else if (action === "save") result = await saveApplied(prev?.unrecorded ?? {}, deps);
        else {
          // A card in the person's own units is set in the unit its numbers are stored in: the list that says which
          // is read first, where the card needs one (units slice 2d).
          const need = needsStoredUnit(block, shownPrefs);
          const units = need ? await cardUnits.read(need) : cardUnits;
          const plan = planApply(block, { ...units, prefs: shownPrefs });
          result = !plan.ok
            ? { kind: "failed", error: { text: plan.reason, code: null } }
            : plan.plan.kind === "acquisition_change"
              ? await applyStudyChanges(block, plan.plan, deps)
              : await approveProposal(plan.plan, deps);
        }
      } catch (err) {
        // The calls say every failure they expect: this is a fault of the page's own (refusalOf logs it).
        result = { kind: "failed", error: refusalOf(err, "Something went wrong on this page.") };
      }
      if (result.kind === "recorded") patchProposal(thread, result.proposal);
      // Decided elsewhere (409, or found so before applying): the card shows the proposal's real state once the whole
      // thread is read again.
      if (result.kind === "decided") await reload(thread);
      setProposal(id, localAfter(action, result));
    } finally {
      inFlight.current.delete(id);
    }
  };

  /**
   * Records a correction or a plant answer as the person (L2), then reads the thread again, so the card shows what was
   * recorded: the server merges it into the card. One at a time per card; a refusal is said on the card.
   */
  const recordCard = async (message: CopilotMessage, blockId: string, send: () => Promise<unknown>) => {
    const key = cardKey(message.id, blockId);
    const thread = threadId;
    if (thread === null || recording[key]) return;
    setRecording((all) => ({ ...all, [key]: true }));
    setCardErrors((all) => without(all, key));
    try {
      // A refusal says it was not recorded; a record that went, and a thread this page could not read again, says so
      // (recordThenReload): never "not recorded" for what was.
      const said = await recordThenReload(send, () => reload(thread), (err) => refusalOf(err, "It was not recorded. Try again.").text);
      if (said !== null) setCardErrors((all) => ({ ...all, [key]: said }));
    } finally {
      setRecording((all) => without(all, key));
    }
  };

  const actions: ThreadActions = {
    canAnswer: canAsk,
    sending,
    answering: sendingWhat?.startsWith("ask:") ? sendingWhat.slice(4) : null,
    answerErrors,
    sentAnswers,
    onAnswer: (message, ask, values) => void sendAnswer(message, ask, values),
    decide: true,
    proposals,
    can,
    tags,
    units: cardUnits,
    onApprove: (block) => void act(block, "approve"),
    onReject: (block, reason) => void act(block, "reject", reason),
    onSave: (block) => void act(block, "save"),
    threadId,
    canRespond: can("knowledge.respond"),
    recording,
    cardErrors,
    onRecordCorrection: (message, block, reason) => {
      if (threadId === null) return;
      const req = correctionRequest(block, threadId, reason);
      if (!req.body) return;
      const body = req.body;
      void recordCard(message, block.id, () => recordEvent.mutateAsync(body));
    },
    onRecordAnswer: (message, block, text) =>
      void recordCard(message, block.id, () => answerQuestion.mutateAsync({ id: block.question_id, body: { text } })),
  };

  // Below the thread: questions to start from (the plant's own only while the thread is empty), and then the chip and
  // the composer.
  const shownStarters = startersShown(starters, items.length, quickAsks);
  const extras = (
    <>
      {state.composer && state.body === "thread" && shownStarters.length > 0 && (
        <StarterChips
          starters={shownStarters}
          compact={items.length > 0}
          disabled={sending}
          onPick={(words) => void sendStarter(words)}
        />
      )}
      {state.composer && !context && text.trim() === "" && file === null && (
        <QuickAsks
          asks={quickAsks}
          disabled={sending}
          onPick={(q) => {
            setText(q.text);
            setContext(q.about);
            setSendError(null);
            textRef.current?.focus();
          }}
        />
      )}
    </>
  );
  const compose = (
    <>
      {state.composer && context && (
        <ContextChip about={context} disabled={sending} onClear={() => setContext(null)} />
      )}
      {state.composer && (
        <Composer
          idBase={idBase}
          text={text}
          onText={(t) => {
            setText(t);
            setSendError(null);
          }}
          onFile={(f) => {
            setFile(f);
            setSendError(null);
          }}
          fileRef={fileRef}
          textRef={textRef}
          problem={problem}
          touched={text.length > 0 || file !== null}
          sending={sendingWhat === "composer"}
          busy={sending}
          sendError={sendError}
          onSend={() => void send()}
        />
      )}
    </>
  );
  const body = (
    // A proposal card shows its values in the org's units and still sends them as the proposal holds them.
    <OrgUnits>
      <ConversationBody
        state={state}
        messages={items}
        jobsById={jobsById}
        canAsk={canAsk}
        onRetry={retry}
        retrying={threads.isFetching || messages.isFetching}
        actions={actions}
        briefAt={briefAt}
      />
    </OrgUnits>
  );

  return (
    <section id={idBase} className={cn("flex flex-col gap-3", contained && "h-full min-h-0")}>
      {heading && (
        <div className="flex flex-wrap items-center gap-2">
          <Bot className="size-4 text-on-surface-muted" />
          <h2 className="text-sm font-semibold text-on-surface">Ask the consultant</h2>
          <span className="text-xs text-on-surface-muted">
            it answers from this org&apos;s records and changes nothing itself: you approve each change it proposes
          </span>
        </div>
      )}
      <Card className={cn("flex flex-col gap-3 p-3", contained && "min-h-0 flex-1 rounded-none border-0 shadow-none")}>
        {canAsk && threadId !== null && items.length > 0 && (
          <div className={cn("flex justify-end", contained && "shrink-0")}>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={sending}
              onClick={() => {
                setFresh(true);
                setCreatedId(null);
                setSendError(null);
              }}
            >
              <MessageSquarePlus className="size-4" />
              New conversation
            </Button>
          </div>
        )}
        {contained ? (
          <>
            {/* The thread scrolls; the composer keeps its place under it, and the questions to start from scroll within
                a third of the screen, so a short screen still leaves the thread room. */}
            <div ref={threadRef} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto" data-conversation-thread>
              {body}
            </div>
            <div className="flex max-h-[30vh] shrink-0 flex-col gap-3 overflow-y-auto empty:hidden">{extras}</div>
            <div className="flex shrink-0 flex-col gap-3">{compose}</div>
          </>
        ) : (
          <>
            {body}
            {extras}
            {compose}
          </>
        )}
      </Card>
    </section>
  );
}
